import * as React from 'react';

import { VgTableMetadata } from 'src/containers/ProjectNewResourceDialogV2/hooks/useVirtualGraphDialogState';
import { ProjectVirtualGraph } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';

type PreviewData = {
  fields: ProjectVirtualGraph['fields'];
  previewRows: ProjectVirtualGraph['previewRows'];
  allResultsIncluded: boolean;
} | null;

type UseEditedFieldsProps = {
  previewTable: string | null;
  isUsingCustomQuery: boolean;
  setVgTableMetadata: React.Dispatch<
    React.SetStateAction<Record<string, VgTableMetadata>>
  >;
  submittedCustomQuery: string | null;
  previewData: PreviewData;
};

type UseEditedFieldsResult = {
  editedResourceName: string | null;
  setEditedResourceName: React.Dispatch<React.SetStateAction<string | null>>;
  editedFieldLabels: Record<string, string>;
  setEditedFieldLabels: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  handleResourceNameChange: (name: string) => void;
  handleFieldLabelChange: (value: string, label: string) => void;
  currentResourceName: string;
  currentFields: ProjectVirtualGraph['fields'];
};

/**
 * Hook to manage edited field names and labels for virtual graph preview.
 * Handles both the local state and syncing changes to table metadata.
 */
export const useEditedFields = ({
  previewTable,
  isUsingCustomQuery,
  setVgTableMetadata,
  submittedCustomQuery,
  previewData,
}: UseEditedFieldsProps): UseEditedFieldsResult => {
  const [editedResourceName, setEditedResourceName] = React.useState<
    string | null
  >(null);
  const [editedFieldLabels, setEditedFieldLabels] = React.useState<
    Record<string, string>
  >({});

  // Handle resource name change
  const handleResourceNameChange = React.useCallback(
    (name: string) => {
      setEditedResourceName(name);
      // Update metadata with edited name if we have a preview table
      if (previewTable && !isUsingCustomQuery) {
        setVgTableMetadata((prev) => {
          const existing = prev[previewTable];
          if (existing) {
            return {
              ...prev,
              [previewTable]: {
                ...existing,
                editedName: name,
              },
            };
          }
          return prev;
        });
      }
    },
    [previewTable, isUsingCustomQuery, setVgTableMetadata]
  );

  // Handle field label change
  const handleFieldLabelChange = React.useCallback(
    (value: string, label: string) => {
      setEditedFieldLabels((prev) => ({
        ...prev,
        [value]: label,
      }));
      // Update metadata with edited field labels if we have a preview table
      if (previewTable && !isUsingCustomQuery) {
        setVgTableMetadata((prev) => {
          const existing = prev[previewTable];
          if (existing) {
            return {
              ...prev,
              [previewTable]: {
                ...existing,
                editedFieldLabels: {
                  ...(existing.editedFieldLabels || {}),
                  [value]: label,
                },
              },
            };
          }
          return prev;
        });
      }
    },
    [previewTable, isUsingCustomQuery, setVgTableMetadata]
  );

  // Get the current resource name (edited or default)
  const currentResourceName = React.useMemo(() => {
    if (editedResourceName !== null) {
      return editedResourceName;
    }
    return (
      previewTable ||
      (submittedCustomQuery
        ? copy.components.projectResourceDialog.resourceTypePanelV2
            .vgResourceCard.customQueryDefaultName
        : '')
    );
  }, [editedResourceName, previewTable, submittedCustomQuery]);

  // Get the current fields with edited labels applied
  const currentFields = React.useMemo(() => {
    if (!previewData?.fields) {
      return [];
    }
    return previewData.fields.map((field) => ({
      ...field,
      label: editedFieldLabels[field.value] ?? field.label,
    }));
  }, [previewData?.fields, editedFieldLabels]);

  return {
    editedResourceName,
    setEditedResourceName,
    editedFieldLabels,
    setEditedFieldLabels,
    handleResourceNameChange,
    handleFieldLabelChange,
    currentResourceName,
    currentFields,
  };
};
